import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Printer, FileText, Download, TrendingUp, Layers } from "lucide-react";
import { Sep911Form } from "@/components/Sep911Form";
import { API_BASE } from "@/lib/api";

export default function Reportes() {
  const [params, setParams] = useSearchParams();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState(params.get("plantel") || "");
  const [ciclo, setCiclo] = useState("2026-2027");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparativa, setComparativa] = useState(null);
  const [modo, setModo] = useState("plantel"); // "plantel" | "consolidado"
  const [consolidado, setConsolidado] = useState(null);
  const [nivelConsolidado, setNivelConsolidado] = useState("PRIMARIA");

  const loadConsolidado = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reportes/consolidado-911", { params: { ciclo, nivel: nivelConsolidado } });
      setConsolidado(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadComparativa = async (pid) => {
    try {
      const { data } = await api.get(`/reportes/comparativa/${pid}`);
      setComparativa(data);
    } catch (e) { /* silent */ }
  };

  const downloadXlsx = async () => {
    try {
      const res = await api.get(`/reportes/plantel/${plantelId}/xlsx`, {
        params: { ciclo },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `911_${report?.plantel?.cct || "plantel"}_${ciclo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        // Solo Primaria activa por ahora
        setPlanteles((data || []).filter((p) => p.nivel === "PRIMARIA"));
        if (!plantelId && data.length) setPlantelId(data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const generar = async () => {
    if (!plantelId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/reportes/plantel/${plantelId}`, { params: { ciclo } });
      setReport(data);
      setParams({ plantel: plantelId });
      loadComparativa(plantelId);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (modo === "consolidado") loadConsolidado();
    else if (plantelId) generar();
  }, [plantelId, ciclo, modo, nivelConsolidado]);

  const print = () => window.print();

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="mb-6 print:hidden">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Reportes</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Formato 911 · vista previa
          </h1>
          <p className="text-sm text-[#545454] mt-1">
            Genere el reporte oficial del censo por plantel y ciclo escolar. Use &quot;Imprimir&quot; para exportar a PDF desde el navegador.
          </p>
        </div>

        <div className="mb-6 flex gap-2 print:hidden">
          <Button
            data-testid="btn-modo-plantel"
            variant={modo === "plantel" ? "default" : "outline"}
            className={modo === "plantel" ? "bg-[#235B4E] hover:bg-[#1D4A40]" : ""}
            onClick={() => setModo("plantel")}
          >
            <FileText className="w-4 h-4 mr-1" /> Por plantel
          </Button>
          <Button
            data-testid="btn-modo-consolidado"
            variant={modo === "consolidado" ? "default" : "outline"}
            className={modo === "consolidado" ? "bg-[#235B4E] hover:bg-[#1D4A40]" : ""}
            onClick={() => setModo("consolidado")}
          >
            <Layers className="w-4 h-4 mr-1" /> Consolidado multi-plantel
          </Button>
        </div>

        <Card className="mb-6 border-[#E5E7EB] print:hidden">
          <CardContent className="p-5 flex flex-wrap items-end gap-4">
            {modo === "plantel" && (
              <div className="min-w-[260px] flex-1">
                <div className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-1.5">Plantel</div>
                <Select value={plantelId} onValueChange={setPlantelId}>
                  <SelectTrigger data-testid="rep-plantel"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                  <SelectContent>
                    {planteles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre} ({p.nivel})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {modo === "consolidado" && (
              <div className="w-44">
                <div className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-1.5">Nivel</div>
                <Select value={nivelConsolidado} onValueChange={setNivelConsolidado}>
                  <SelectTrigger data-testid="rep-nivel-consolidado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRIMARIA">Primaria</SelectItem>
                    <SelectItem value="PREESCOLAR">Preescolar</SelectItem>
                    <SelectItem value="SECUNDARIA">Secundaria</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="w-40">
              <div className="text-xs uppercase tracking-wider text-[#9CA3AF] mb-1.5">Ciclo</div>
              <Select value={ciclo} onValueChange={setCiclo}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024-2025">2024-2025</SelectItem>
                  <SelectItem value="2025-2026">2025-2026</SelectItem>
                  <SelectItem value="2026-2027">2026-2027</SelectItem>
                  <SelectItem value="2027-2028">2027-2028</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="btn-generar"
              onClick={modo === "consolidado" ? loadConsolidado : generar}
              disabled={loading || (modo === "plantel" && !plantelId)}
              className="bg-[#235B4E] hover:bg-[#1D4A40]"
            >
              <FileText className="w-4 h-4 mr-1" />
              {loading ? "Generando…" : "Actualizar"}
            </Button>
            <Button
              data-testid="btn-print"
              variant="outline"
              onClick={print}
              disabled={modo === "plantel" ? !report : !consolidado}
            >
              <Printer className="w-4 h-4 mr-1" /> Imprimir / PDF
            </Button>
            {modo === "plantel" && (
              <Button
                data-testid="btn-xlsx"
                onClick={downloadXlsx}
                disabled={!report}
                className="bg-[#235B4E] hover:bg-[#1D4A40] text-white"
              >
                <Download className="w-4 h-4 mr-1" /> Excel (.xlsx)
              </Button>
            )}
          </CardContent>
        </Card>

        {comparativa && comparativa.ciclos.length > 0 && modo === "plantel" && (() => {
          const startYear = parseInt((ciclo || "").split("-")[0], 10);
          const targets = isNaN(startYear) ? [] : [
            `${startYear - 1}-${startYear}`,
            `${startYear}-${startYear + 1}`,
            `${startYear + 1}-${startYear + 2}`,
          ];
          const visibles = comparativa.ciclos.filter((c) => targets.includes(c.ciclo));
          if (visibles.length === 0) return null;
          return (
          <Card className="mb-6 border-[#E5E7EB] print:hidden">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#235B4E]" />
                <div className="text-sm font-semibold text-[#10312B]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Comparativa entre ciclos escolares
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {visibles.map((c) => {
                  const activo = c.ciclo === ciclo;
                  return (
                    <button
                      key={c.ciclo}
                      onClick={() => setCiclo(c.ciclo)}
                      data-testid={`cmp-${c.ciclo}`}
                      className={`text-left border rounded p-3 transition-colors ${
                        activo
                          ? "border-[#235B4E] bg-[#235B4E]/5"
                          : "border-[#E5E7EB] hover:border-[#235B4E]/40"
                      }`}
                    >
                      <div className="text-xs uppercase tracking-wider text-[#9CA3AF]">Ciclo</div>
                      <div className="text-lg font-semibold text-[#10312B]">{c.ciclo}</div>
                      <div className="mt-2 text-xs space-y-0.5">
                        <div className="flex justify-between">
                          <span className="text-[#545454]">Alumnos:</span>
                          <span className="font-semibold">{c.total_alumnos}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#545454]">Grupos:</span>
                          <span className="font-semibold">{c.total_grupos}</span>
                        </div>
                        <div className="flex justify-between text-[10px] text-[#6B7280]">
                          <span>{c.hombres} H · {c.mujeres} M</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          );
        })()}

        {planteles.length === 0 && (
          <div className="p-4 border border-[#BC955C]/40 bg-[#BC955C]/10 rounded text-sm">
            Registre al menos un plantel para generar el reporte.
          </div>
        )}

        {modo === "plantel" && report && (
          <div className="print:m-0">
            <Sep911Form report={report} />
          </div>
        )}

        {modo === "consolidado" && consolidado && (
          <div className="print:m-0">
            <Sep911Form report={consolidado} />
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function ConsolidadoView_LEGACY_UNUSED({ data }) {
  const { ciclo, planteles, totales } = data;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden shadow print:shadow-none print:border-0" data-testid="consolidado-view">
      <div className="flex h-1.5">
        <div className="flex-1 bg-[#235B4E]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#9F2241]" />
      </div>
      <header className="bg-[#10312B] text-white px-6 py-4">
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">
          Sistema Censo 911 · Tamaulipas
        </div>
        <div className="text-lg font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Reporte 911 Consolidado — Ciclo {ciclo}
        </div>
        <div className="text-xs text-white/70 mt-1">
          {totales.planteles} plantel(es) · {totales.alumnos} alumnos activos · {totales.grupos} grupos
        </div>
      </header>

      <div className="p-6 text-[12px]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <table className="w-full border-collapse border border-[#98989A]">
          <thead>
            <tr className="bg-[#10312B] text-white text-[11px]">
              <th className="border border-[#98989A] p-2 text-left">CCT</th>
              <th className="border border-[#98989A] p-2 text-left">Plantel</th>
              <th className="border border-[#98989A] p-2">Nivel</th>
              <th className="border border-[#98989A] p-2">Grupos</th>
              <th className="border border-[#98989A] p-2">Hombres</th>
              <th className="border border-[#98989A] p-2">Mujeres</th>
              <th className="border border-[#98989A] p-2">Total</th>
              <th className="border border-[#98989A] p-2">Becas</th>
              <th className="border border-[#98989A] p-2">Discapacidad</th>
            </tr>
          </thead>
          <tbody>
            {planteles.length === 0 ? (
              <tr>
                <td colSpan={9} className="border border-[#98989A] p-4 text-center text-[#6B7280]">
                  No hay planteles registrados
                </td>
              </tr>
            ) : planteles.map((row) => (
              <tr key={row.plantel.id} className="text-center">
                <td className="border border-[#98989A] p-1.5 text-left font-mono text-xs">{row.plantel.cct}</td>
                <td className="border border-[#98989A] p-1.5 text-left font-medium">{row.plantel.nombre}</td>
                <td className="border border-[#98989A] p-1.5">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#235B4E]/10 text-[#235B4E]">
                    {row.plantel.nivel}
                  </span>
                </td>
                <td className="border border-[#98989A] p-1.5">{row.grupos}</td>
                <td className="border border-[#98989A] p-1.5">{row.hombres}</td>
                <td className="border border-[#98989A] p-1.5">{row.mujeres}</td>
                <td className="border border-[#98989A] p-1.5 font-semibold">{row.alumnos}</td>
                <td className="border border-[#98989A] p-1.5">{row.becas}</td>
                <td className="border border-[#98989A] p-1.5">{row.discapacidad}</td>
              </tr>
            ))}
            <tr className="bg-[#F3F4F6] font-semibold text-center">
              <td className="border border-[#98989A] p-2 text-left" colSpan={3}>TOTAL DEL SISTEMA</td>
              <td className="border border-[#98989A] p-2">{totales.grupos}</td>
              <td className="border border-[#98989A] p-2 text-[#235B4E]">{totales.hombres}</td>
              <td className="border border-[#98989A] p-2 text-[#9F2241]">{totales.mujeres}</td>
              <td className="border border-[#98989A] p-2 text-[#10312B]">{totales.alumnos}</td>
              <td className="border border-[#98989A] p-2">{totales.becas}</td>
              <td className="border border-[#98989A] p-2">{totales.discapacidad}</td>
            </tr>
          </tbody>
        </table>
        <div className="text-right text-[10px] text-[#6B7280] mt-4">
          Fecha de generación: {new Date(data.generado_en).toLocaleDateString("es-MX")}
        </div>
      </div>
    </div>
  );
}
